import { useState } from 'react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import moment from 'moment'

import {
  fetchEmployeeHomeAddresses,
  putUpdateEmployee,
} from '../payrollActions'
import { DetailsRow } from './JobPayTab'
import {
  selectEmployeeByUuid,
  selectJobByEmployeeUuid,
} from '../payroll.selectors'
import { gepAddressToText } from '../helpers'
import { Button, Text, Form, Dialog, Card, CardColors } from '@/ui'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { EmployeeWithoutJob } from '../employee.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import {
  makeDateSchema,
  makeReqStringSchema,
  makeReqEmailSchema,
} from '@/componentsV1/BaseComponents'

const END_VAL = 'for this employee'
const validationSchema = yup.object({
  firstName: makeReqStringSchema({ field: 'first name', end: END_VAL }),
  lastName: makeReqStringSchema({ field: 'last name', end: END_VAL }),
  dob: makeDateSchema({ field: 'date of birth', end: END_VAL }),
  email: makeReqEmailSchema({ field: 'email', end: END_VAL }),
})

const EditDetailsModal = ({
  employee,
  closeModal,
}: {
  employee: EmployeeWithoutJob
  closeModal: () => void
}) => {
  const dispatch = useAppDispatch()

  const formik = useFormik({
    initialValues: {
      firstName: employee.first_name,
      lastName: employee.last_name,
      dob: moment(employee.date_of_birth, DATE_FORMATS.GUSTO_SUBMIT).format(
        DATE_FORMATS.INPUT
      ),
      email: employee.email || '',
    },
    validationSchema,
    onSubmit: async ({ firstName, lastName, email, dob }) => {
      const res = await putUpdateEmployee(employee.uuid, {
        version: employee.version,
        first_name: firstName,
        last_name: lastName,
        date_of_birth: moment(dob, DATE_FORMATS.INPUT).format(
          DATE_FORMATS.GUSTO_SUBMIT
        ),
        email,
      })(dispatch)

      if (res) {
        closeModal()
      }
    },
  })

  return (
    <Dialog open onClose={closeModal}>
      <Dialog.Title>Update Details</Dialog.Title>
      <Dialog.Content>
        <FormikProvider value={formik}>
          <div className="flex flex-col gap-4">
            <Form.Input name="firstName" label="First Name" required />
            <Form.Input name="lastName" label="Last Name" required />
            <Form.DateInput name="dob" label="Date of Birth" required />
            <Form.Input name="email" type="email" label="Email" required />
          </div>
          <Dialog.Footer className="mt-4">
            <Button onClick={formik.submitForm}>Confirm</Button>
            <Button onClick={closeModal} variant="secondary">
              Cancel
            </Button>
          </Dialog.Footer>
        </FormikProvider>
      </Dialog.Content>
    </Dialog>
  )
}

export const EmployeeDetailsPanel = ({
  employeeUuid,
}: {
  employeeUuid: string
}) => {
  const [modalOpen, setIsModalOpen] = useState(false)
  const employee = useReselector(selectEmployeeByUuid, employeeUuid)
  const job = useReselector(selectJobByEmployeeUuid, employeeUuid)
  const homeAddressList = useFetchResponse(
    fetchEmployeeHomeAddresses,
    employeeUuid
  )

  const currentHomeAddress = homeAddressList?.find((address) => address.active)

  if (!employee) {
    return null
  }

  const { first_name, last_name, date_of_birth, email, terminated } = employee

  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between">
          <Text variant={TypographyStyles.H2}>Employee Details</Text>
          <Button onClick={() => setIsModalOpen(true)} variant="link">
            Edit
          </Button>
        </div>

        <DetailsRow label="First Name" value={first_name} />
        <DetailsRow label="Last Name" value={last_name} />
        <DetailsRow
          label="Date of Birth"
          value={moment(date_of_birth).format(DATE_FORMATS.DISPLAY_LONG)}
        />
        <DetailsRow label="Personal Email" value={email} />
        <DetailsRow
          label="Employee Status"
          value={terminated ? 'Inactive' : 'Active'}
        />
        {job && (
          <DetailsRow
            label="Start Date"
            value={moment(job.hire_date).format(DATE_FORMATS.DISPLAY_LONG)}
          />
        )}
        {currentHomeAddress?.street_1 && (
          <DetailsRow
            label="Home address"
            value={gepAddressToText(currentHomeAddress)}
          />
        )}
      </div>

      {modalOpen && (
        <EditDetailsModal
          employee={employee}
          closeModal={() => setIsModalOpen(false)}
        />
      )}
    </Card>
  )
}
